import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { UpdatePreferencesPayload, useScreenOrientation } from "@is/hooks";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  AttributeSetting,
  AttributeType,
  PreferenceFilter,
  PreferenceOption,
  SearchFunctionProperties,
  SearchTerm,
  SearchTermType,
} from "@is/types";
import {
  SEARCH_INPUT_TEXT_AND_NUMBERS,
  convertTextToAttributes,
  getAppliedPreferenceOptions,
  getSelectedTab,
  getSortedSearchTermsByLastRemove,
  unselectSingleSelectedAttributes,
} from "@is/utils";

type Props = {
  attributes: AttributeSetting[];
  preferences: PreferenceOption[];
  updatePreferences: (
    params: UpdatePreferencesPayload
  ) => Promise<PreferenceOption[]>;
  filters: PreferenceFilter[];
  handleSearch: ({
    newSearchTerms,
    pageNumber,
    sectionId,
  }: SearchFunctionProperties) => void;
};

const useManageSearchBox = ({
  attributes,
  preferences,
  updatePreferences,
  filters,
  handleSearch,
}: Props) => {
  const { t } = useTranslation(TranslationContext.ProductListing);
  const [searchValue, setSearchValue] = useState("");

  const { updateViewState, searchTerms, selectedTab, tabs } =
    useContext(ViewStateContext).content;

  const { isPortraitView } = useScreenOrientation();

  const primaryAttributes = useMemo(
    () => attributes?.filter((attribute) => attribute.primary),
    [attributes]
  );

  const singleSelectionAttributes = useMemo(
    () => attributes?.filter((attribute) => attribute.singleSelection),
    [attributes]
  );

  const searchTermsWithoutSizesAndLength = useMemo(
    () =>
      searchTerms.filter(
        (searchTerm) =>
          searchTerm.type !== SearchTermType.Size &&
          searchTerm.type !== SearchTermType.Length &&
          searchTerm.type !== SearchTermType.Price &&
          !searchTerm.isPrimary
      ),
    [searchTerms]
  );

  const searchTermsValues = useMemo(
    () => searchTerms.map(({ value }: SearchTerm) => value.toLowerCase()),
    [searchTerms]
  );

  const selectedSizes = useMemo(
    () =>
      searchTerms.filter(
        (searchTerm) => searchTerm.type === SearchTermType.Size
      ),
    [searchTerms]
  );

  const selectedLength = useMemo(
    () =>
      searchTerms.filter(
        (searchTerm) => searchTerm.type === SearchTermType.Length
      ),
    [searchTerms]
  );

  const sizesOrLengthsSelected = useMemo(
    () => !!selectedSizes.length || !!selectedLength.length,
    [selectedSizes, selectedLength]
  );

  const sizesAndLengthsSelected = useMemo(
    () => !!selectedSizes.length && !!selectedLength.length,
    [selectedSizes, selectedLength]
  );

  const selectedSizesValue = useMemo(
    () => selectedSizes.map((size) => size.value).join(", "),
    [selectedSizes]
  );

  const selectedLengthsValue = useMemo(
    () => selectedLength.map((length) => length.value).join(", "),
    [selectedLength]
  );

  const handleClearAll = useCallback(async () => {
    const primarySearchTerms = searchTerms.filter(
      (searchTerm) => searchTerm.isPrimary
    );

    updateViewState({
      searchTerms: primarySearchTerms,
      selectedTab: null,
      priceRange: { min: null, max: null },
    });

    await updatePreferences({
      filters,
      appliedPreferenceOptions: getAppliedPreferenceOptions(
        primarySearchTerms,
        preferences
      ),
    });

    if (primarySearchTerms.length) {
      handleSearch({ newSearchTerms: primarySearchTerms });
    }
  }, [
    filters,
    handleSearch,
    preferences,
    searchTerms,
    updatePreferences,
    updateViewState,
  ]);

  const handleTabSelect = useCallback(() => {
    updateViewState({ selectedTab: AttributeType.Sizes });
  }, [updateViewState]);

  const handleTagRemove = useCallback(
    async (event, tag: SearchTerm) => {
      const filteredSearchTerms = getSortedSearchTermsByLastRemove(
        searchTerms,
        preferences,
        tag
      );

      const filteredPreferences = preferences?.filter(
        (preference) =>
          tabs?.some((tab) => tab.key === preference.key) &&
          preference?.key !== SearchTermType.Length
      );

      const preSelectedTab = getSelectedTab(
        filteredPreferences,
        filteredSearchTerms
      );

      // Reset price range if price tag is removed
      const isPriceTag = tag.type === SearchTermType.Price;

      updateViewState({
        searchTerms: filteredSearchTerms,
        selectedTab: preSelectedTab || selectedTab,
        ...(isPriceTag && { priceRange: { min: null, max: null } }),
      });

      await updatePreferences({
        filters,
        appliedPreferenceOptions: getAppliedPreferenceOptions(
          filteredSearchTerms,
          preferences
        ),
      });

      trackEvent({
        eventType: TrackingEventTypes.SearchTermRemoved,
        data: { terms: [tag] },
      });

      handleSearch({ newSearchTerms: filteredSearchTerms });
    },
    [
      searchTerms,
      preferences,
      selectedTab,
      updateViewState,
      updatePreferences,
      filters,
      handleSearch,
      tabs,
    ]
  );

  const handleTagClick = useCallback(
    (event, key: string) => {
      updateViewState({ selectedTab: key });
    },
    [updateViewState]
  );

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(
        SEARCH_INPUT_TEXT_AND_NUMBERS,
        ""
      );

      setSearchValue(value);
    },
    []
  );

  const handleFind = useCallback(async () => {
    const trimmedSearchValue = searchValue.trim();

    if (searchTermsValues.includes(trimmedSearchValue.toLowerCase())) {
      setSearchValue("");
      return;
    }

    const attributes = convertTextToAttributes(trimmedSearchValue, preferences);

    let prevSearchTerms = searchTerms;
    if (attributes.length) {
      const searchedAttribute = attributes[0];

      prevSearchTerms = unselectSingleSelectedAttributes(
        searchedAttribute.key,
        searchedAttribute.type,
        singleSelectionAttributes,
        searchTerms,
        preferences
      );
    }

    const attributesWithPrimaryFlag = attributes.map((attribute) => ({
      ...attribute,
      ...(primaryAttributes.some(
        (primary) => attribute.key === primary.key
      ) && { isPrimary: true }),
    }));

    const updatedAttributes =
      attributesWithPrimaryFlag?.length > 1
        ? attributesWithPrimaryFlag.map((attribute: SearchTerm) => ({
            ...attribute,
            label: `${attribute.type}: ${attribute.value}`,
          }))
        : attributesWithPrimaryFlag;

    const newSearchTerms = attributes?.length
      ? [...prevSearchTerms, ...updatedAttributes]
      : [
          ...prevSearchTerms,
          {
            key: trimmedSearchValue,
            value: trimmedSearchValue,
            type: SearchTermType.Text,
            label: trimmedSearchValue,
          },
        ];

    updateViewState({
      searchTerms: newSearchTerms,
    });

    await updatePreferences({
      filters,
      appliedPreferenceOptions: getAppliedPreferenceOptions(
        newSearchTerms,
        preferences
      ),
    });

    handleSearch({ newSearchTerms });
    trackEvent({
      eventType: TrackingEventTypes.SearchTermAdded,
      data: {
        terms: attributes?.length
          ? updatedAttributes
          : [
              {
                key: trimmedSearchValue,
                value: trimmedSearchValue,
                type: SearchTermType.Text,
                label: trimmedSearchValue,
              },
            ],
      },
    });

    setSearchValue("");
  }, [
    primaryAttributes,
    singleSelectionAttributes,
    searchValue,
    searchTermsValues,
    preferences,
    updateViewState,
    searchTerms,
    updatePreferences,
    filters,
    handleSearch,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const trimmedSearchValue = searchValue.trim();

      if (event.key === "Enter" && trimmedSearchValue) {
        handleFind();
      }
    },
    [searchValue, handleFind]
  );

  return {
    searchValue,
    isPortraitView,
    handleSearchValueChange,
    handleKeyDown,
    handleFind,
    sizesOrLengthsSelected,
    handleTabSelect,
    selectedSizesValue,
    sizesAndLengthsSelected,
    selectedLengthsValue,
    searchTermsWithoutSizesAndLength,
    handleTagRemove,
    handleTagClick,
    handleClearAll,
    t,
  };
};

export default useManageSearchBox;
